// The metrics a cell can show. `footprint` ones count rows the company created, which a
// feature without footprint entities has no measure of. Overall folds the in-window ones
// into a single score (see scoreCells).
export const METRICS = [
  { key: 'overall', label: 'Overall' },
  { key: 'active_users', label: 'Active users' },
  { key: 'active_days', label: 'Active days' },
  { key: 'writes', label: 'Writes' },
  { key: 'requests', label: 'Requests' },
  { key: 'created_in_window', label: 'Data created', footprint: true },
  { key: 'total', label: 'Data total', footprint: true }
];

export const DEFAULT_METRIC = 'overall';

// The metrics the overall score averages. Data total is left out: it counts rows from before
// the window, so it would score a company for use it no longer makes.
const OVERALL_PARTS = ['active_users', 'active_days', 'writes', 'requests', 'created_in_window'];

export const OVERALL_DESCRIPTION = 'How a company ranks against the others on active users, active days, writes, requests and data created, averaged into one score out of 100.';

export const SCALES = [
  { key: 'relative', label: 'Ranked' },
  { key: 'raw', label: 'Raw' }
];

export const DEFAULT_SCALE = 'relative';

// Five bands of one blue, far enough apart to tell the top fifth from the bottom at a glance.
// Both themes run from the palest blue at the bottom to the deepest at the top, so the most
// active cells carry the most colour.
export const BANDS = [
  'bg-[#86b6ef] text-[#0d1b2e] dark:bg-[#cde2fb] dark:text-[#0d1b2e]',
  'bg-[#5598e7] text-[#0d1b2e] dark:bg-[#86b6ef] dark:text-[#0d1b2e]',
  'bg-[#2a78d6] text-white dark:bg-[#3987e5] dark:text-white',
  'bg-[#1c5cab] text-white dark:bg-[#256abf] dark:text-white',
  'bg-[#0d366b] text-white dark:bg-[#184f95] dark:text-white'
];

export const BAND_LABELS = {
  relative: ['Bottom 20%', '20–40%', '40–60%', '60–80%', 'Top 20%'],
  raw: ['Up to 20%', '20–40%', '40–60%', '60–80%', '80–100%']
};

export const LEGEND_CAPTIONS = {
  relative: 'Rank among the companies that use it',
  raw: 'Share of the largest value in the table'
};

// Fill and text classes for an intensity in [0, 1].
export const bandClass = (intensity) => BANDS[Math.min(BANDS.length - 1, Math.floor(intensity * BANDS.length))];

// One column per feature, or per area when collapsed. A column lists the features it covers.
export const buildColumns = (catalogue, collapsed) => {
  if (!collapsed) {
    return catalogue.map((feature) => ({ key: feature.key, label: feature.label, area: feature.area, areaLabel: feature.area_label, features: [feature] }));
  }
  const areas = [];
  catalogue.forEach((feature) => {
    const area = areas.find((a) => a.key === feature.area);
    if (area) {
      area.features.push(feature);
    } else {
      areas.push({ key: feature.area, label: feature.area_label, area: feature.area, areaLabel: feature.area_label, features: [feature] });
    }
  });
  return areas;
};

// Runs of adjacent columns in the same area, for the grouped header row.
export const buildGroups = (columns) =>
  columns.reduce((groups, column) => {
    const last = groups[groups.length - 1];
    if (last && last.area === column.area) {
      last.span += 1;
    } else {
      groups.push({ area: column.area, label: column.areaLabel, span: 1 });
    }
    return groups;
  }, []);

const maxDate = (a, b) => {
  if (!a) return b;
  if (!b) return a;
  return new Date(a) > new Date(b) ? a : b;
};

// A company's metrics for a column. Requests and writes add up across the column's features;
// active users and days are distinct counts, so the column takes the largest rather than
// a sum that would count the same person or day twice. Footprint counts are null when no
// feature in the column has a footprint.
export const cellFor = (companyUsage, column) => {
  const tracked = column.features.filter((feature) => feature.footprint_entities);
  return column.features.reduce(
    (cell, feature) => {
      const entry = companyUsage?.[feature.key];
      if (!entry) return cell;
      const footprint = entry.footprint;
      return {
        requests: cell.requests + entry.requests,
        writes: cell.writes + entry.writes,
        active_users: Math.max(cell.active_users, entry.active_users),
        active_days: Math.max(cell.active_days, entry.active_days),
        created_in_window: footprint ? cell.created_in_window + footprint.created_in_window : cell.created_in_window,
        total: footprint ? cell.total + footprint.total : cell.total,
        last_created: footprint ? maxDate(cell.last_created, footprint.last_created) : cell.last_created
      };
    },
    {
      requests: 0,
      writes: 0,
      active_users: 0,
      active_days: 0,
      created_in_window: tracked.length ? 0 : null,
      total: tracked.length ? 0 : null,
      last_created: null
    }
  );
};

// The share of the non-zero values at or below this one, so the only company using a
// feature scores as fully as the heaviest user of a popular one.
const rankIn = (sorted, value) => (value > 0 ? sorted.filter((v) => v <= value).length / sorted.length : 0);

const sortedNonZero = (values) => values.filter((value) => value > 0).sort((a, b) => a - b);

// Where a value sits between the lowest (0) and highest (1) of the non-zero values, so first
// and last place always land in the top and bottom bands. A lone value is the highest.
const positionIn = (sorted, value) => (sorted.length > 1 ? sorted.filter((v) => v < value).length / (sorted.length - 1) : 1);

// companyUid -> intensity in [0, 1] for each value above zero. Relative: its position among
// the non-zero values. Raw: the value against `max`.
export const intensitiesFor = (valuesByUid, scale, max) => {
  const sorted = sortedNonZero(Object.values(valuesByUid));
  return Object.fromEntries(
    Object.entries(valuesByUid)
      .filter(([, value]) => value > 0)
      .map(([uid, value]) => [uid, scale === 'raw' ? value / max : positionIn(sorted, value)])
  );
};

// columnKey -> companyUid -> intensity, for the cells with a value above zero. Raw compares
// against the largest cell in the whole table.
export const buildIntensities = (rows, columns, metric, scale) => {
  const globalMax = Math.max(0, ...rows.flatMap((row) => columns.map((column) => row.cells[column.key][metric] ?? 0)));

  return Object.fromEntries(
    columns.map((column) => [column.key, intensitiesFor(Object.fromEntries(rows.map((row) => [row.uid, row.cells[column.key][metric] ?? 0])), scale, globalMax)])
  );
};

// companyUid -> columnKey -> cell, each cell carrying an `overall` score out of 100: its rank
// among every company on each in-window metric it tracks, averaged. Ranked against all
// companies rather than the visible ones, so filtering never changes a company's score.
export const scoreCells = (companies, usage, columns) => {
  const cells = Object.fromEntries(companies.map((company) => [company.uid, Object.fromEntries(columns.map((column) => [column.key, cellFor(usage[company.uid], column)]))]));

  columns.forEach((column) => {
    const sorted = Object.fromEntries(OVERALL_PARTS.map((part) => [part, sortedNonZero(companies.map((company) => cells[company.uid][column.key][part] ?? 0))]));
    companies.forEach((company) => {
      const cell = cells[company.uid][column.key];
      const parts = OVERALL_PARTS.filter((part) => cell[part] !== null);
      const mean = parts.reduce((sum, part) => sum + rankIn(sorted[part], cell[part]), 0) / parts.length;
      // Any use at all scores at least 1, so it never reads as unused
      cell.overall = mean > 0 ? Math.max(1, Math.round(mean * 100)) : 0;
    });
  });

  return cells;
};

// companyUid -> overall score out of 100: the mean of its per-feature scores across every
// feature, so using more of the product counts as much as using one part of it heavily.
export const companyScores = (featureCells, catalogue) =>
  Object.fromEntries(
    Object.entries(featureCells).map(([uid, cells]) => [
      uid,
      catalogue.length ? Math.round(catalogue.reduce((sum, feature) => sum + cells[feature.key].overall, 0) / catalogue.length) : 0
    ])
  );

export const formatNumber = (num) => {
  if (num === null || num === undefined) return '—';
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 10000) return `${(num / 1000).toFixed(0)}K`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toLocaleString();
};
